import React, { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/auth";
import { LHSLogo } from "@/logos";
import { useTranslation } from "react-i18next";
import { setLanguage } from "@/i18n";
import { useUnseenOrders } from "@/hooks/useUnseenOrders";
import {
  LayoutDashboard, HardDrive, KeyRound, History, Settings as SettingsIcon,
  LogOut, ServerCog, CreditCard, Menu, X, ShoppingCart, Award, ShieldCheck,
} from "lucide-react";

const menu = [
  { to: "/", icon: LayoutDashboard, tkey: "nav_dashboard", id: "dashboard", end: true },
  { to: "/firmware", icon: HardDrive, tkey: "nav_firmware", id: "firmware" },
  { to: "/store", icon: ShoppingCart, tkey: "nav_store", id: "store" },
  { to: "/mes-licences-tierces", icon: Award, tkey: "nav_my_3p_licenses", id: "mes-licences-3p" },
  { to: "/licences", icon: KeyRound, tkey: "nav_licences", id: "licences" },
  { to: "/paiements", icon: CreditCard, tkey: "nav_payments", id: "paiements" },
  { to: "/historique", icon: History, tkey: "nav_history", id: "historique" },
  { to: "/parametres", icon: SettingsIcon, tkey: "nav_settings", id: "parametres" },
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const { t, i18n } = useTranslation();
  const [mobileOpen, setMobileOpen] = useState(false);

  // auto-close drawer on route change
  useEffect(() => { setMobileOpen(false); }, [loc.pathname]);

  const isLicActive = user?.license_status === "active";
  const isAdmin = user?.role === "admin";
  const unseen = useUnseenOrders(isAdmin, true);
  const expDays = user?.license_expires_at
    ? Math.max(0, Math.ceil((new Date(user.license_expires_at) - new Date()) / (1000 * 60 * 60 * 24)))
    : null;

  const SidebarInner = (
    <>
      <div className="p-5 border-b border-zinc-800/80 flex items-center justify-between gap-3">
        <LHSLogo size={44} />
        <button
          data-testid="sidebar-close"
          onClick={() => setMobileOpen(false)}
          className="md:hidden text-zinc-400 hover:text-white"
          aria-label="Fermer le menu"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
      <nav className="flex-1 p-3 space-y-1 scroll-thin overflow-y-auto">
        {menu.map((m) => {
          const cls = ({ isActive }) =>
            `group flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all ${
              isActive
                ? "bg-orange-500/10 text-orange-400 border border-orange-500/30 orange-glow"
                : "text-zinc-400 hover:bg-zinc-900 hover:text-white border border-transparent"
            }`;
          return (
            <NavLink
              key={m.id} to={m.to} end={m.end}
              data-testid={`sidebar-nav-${m.id}`}
              className={cls}
            >
              <m.icon className="w-4 h-4" />
              <span className="font-medium flex-1">{t(m.tkey)}</span>
            </NavLink>
          );
        })}
        {isAdmin && (
          <NavLink
            to="/admin"
            data-testid="sidebar-nav-admin"
            className={({ isActive }) =>
              `mt-3 flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm border transition-all ${
                isActive
                  ? "bg-orange-500/15 text-orange-400 border-orange-500/40 orange-glow"
                  : "text-zinc-300 border-zinc-800 hover:border-orange-500/40 hover:text-orange-400"
              }`
            }
          >
            <ServerCog className="w-4 h-4" />
            <span className="flex-1">{t("nav_admin")}</span>
            {unseen > 0 && <span data-testid="sidebar-admin-badge" className="min-w-5 h-5 px-1.5 rounded-full bg-orange-500 text-black text-[10px] font-bold inline-flex items-center justify-center">{unseen}</span>}
          </NavLink>
        )}
      </nav>
      <div className="p-3 border-t border-zinc-800/80">
        <div className="card-lhs p-3 mb-3">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono-tech">{t("nav_settings")}</div>
          <div className="text-sm font-semibold truncate">{user?.name}</div>
          <div className="text-[11px] text-zinc-500 truncate">{user?.email}</div>
          <div className="flex items-center justify-between mt-2 text-[11px]">
            <span className={`brand-pill ${isLicActive ? "text-emerald-400" : "text-red-400"}`}>
              <ShieldCheck className="w-3 h-3" />
              {isLicActive ? t("active") : t(user?.license_status === "suspended" ? "suspended" : "inactive")}
            </span>
            {isLicActive && expDays !== null && (
              <span className="text-zinc-500 font-mono-tech">{expDays}j</span>
            )}
          </div>
        </div>
        <button
          data-testid="logout-btn"
          onClick={() => { logout(); nav("/login"); }}
          className="w-full flex items-center justify-center gap-2 text-sm text-zinc-400 hover:text-red-400 border border-zinc-800 hover:border-red-500/40 rounded-lg py-2 transition-all"
        >
          <LogOut className="w-4 h-4" /> {t("logout")}
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-black text-white flex">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex w-64 shrink-0 border-r border-zinc-800/80 bg-[#09090B] flex-col" data-testid="sidebar">
        {SidebarInner}
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex" data-testid="mobile-drawer">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
          <aside className="relative w-72 max-w-[85vw] bg-[#09090B] border-r border-zinc-800/80 flex flex-col animate-in slide-in-from-left duration-200">
            {SidebarInner}
          </aside>
        </div>
      )}

      <main className="flex-1 min-w-0 flex flex-col">
        <header className="h-14 border-b border-zinc-800/80 bg-[#09090B]/80 backdrop-blur px-3 sm:px-6 flex items-center justify-between gap-2" data-testid="topbar">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <button
              data-testid="mobile-menu-btn"
              onClick={() => setMobileOpen(true)}
              className="md:hidden p-2 -ml-2 rounded-lg text-zinc-300 hover:text-white hover:bg-zinc-800"
              aria-label="Ouvrir le menu"
            >
              <Menu className="w-5 h-5" />
            </button>
            <div className="md:hidden"><LHSLogo size={32} /></div>
            <div className="hidden sm:flex items-center gap-3 text-sm min-w-0">
              <span className="pulse-dot" />
              <span className="text-zinc-400 font-mono-tech text-xs">{t("server_connected")}</span>
              <span className="text-zinc-600">|</span>
              <span className="text-zinc-500 font-mono-tech text-xs">BUILD 1.2 PRO</span>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="flex items-center gap-1" data-testid="topbar-lang">
              {["fr","en","ar"].map(l => (
                <button key={l}
                  data-testid={`topbar-lang-${l}`}
                  onClick={() => setLanguage(l)}
                  className={`text-[10px] font-mono-tech px-2 py-1 rounded border ${i18n.language === l ? "border-orange-500 text-orange-400" : "border-zinc-800 text-zinc-500 hover:text-white"}`}>
                  {l.toUpperCase()}
                </button>
              ))}
            </div>
            <span className="hidden sm:inline-flex brand-pill text-orange-400"><span className="w-1.5 h-1.5 rounded-full bg-orange-500" /> {user?.role?.toUpperCase()}</span>
          </div>
        </header>
        {user?.license_status === "suspended" && loc.pathname !== "/paiements" && loc.pathname !== "/parametres" && (
          <div className="bg-red-500/10 border-b border-red-500/40 text-red-300 text-xs sm:text-sm px-3 sm:px-6 py-2 flex flex-col sm:flex-row sm:items-center justify-between gap-1" data-testid="suspended-banner">
            <span>⚠ Votre licence a été suspendue. Contactez l'administration.</span>
            <button onClick={() => nav("/paiements")} className="underline hover:text-red-200 text-left sm:text-right">{t("license_go_pay")}</button>
          </div>
        )}
        <div className="flex-1 overflow-auto scroll-thin">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
